import React, { useState } from 'react'
import { Plus, Eye, Edit, Trash2, Link2, Globe } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { useHasPermission } from '../../hooks/usePermissions'
import NewSiteModal from './NewSiteModal'

// Gradient colors for site thumbnails (inline styles since Tailwind is removed)
const BANNER_GRADIENTS = [
  ['#8b5cf6', '#6d28d9'],
  ['#6c63ff', '#5940e6'],
  ['#f43f5e', '#ec4899'],
  ['#10b981', '#0891b2'],
  ['#f97316', '#f59e0b'],
  ['#3b82f6', '#6366f1'],
  ['#ec4899', '#f43f5e'],
  ['#14b8a6', '#06b6d4'],
]

const getBannerGradient = (name = '') => {
  const index = (name.charCodeAt(0) || 0) % BANNER_GRADIENTS.length
  return BANNER_GRADIENTS[index]
}

const getRelativeTime = (timestamp) => {
  if (!timestamp) return '—'
  const diff = Date.now() - new Date(timestamp)
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1)  return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7)  return `${d}d ago`
  return new Date(timestamp).toLocaleDateString('en-GB')
}

const getSiteEditorLogo = (site) => {
  try {
    const meta = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : (site.meta_data || {})
    return meta.logo || null
  } catch {
    return null
  }
}

// ── Site card ──────────────────────────────────────────────────────────────
const SiteCard = ({ site, canEditText, canDeleteSites, onDelete }) => {
  const isPublished = site.status === 'published'
  const [gradStart, gradEnd] = getBannerGradient(site.name)
  const logoUrl = getSiteEditorLogo(site)
  const domain = site.custom_domain || site.azure_published_url?.replace(/^https?:\/\//, '') || '—'
  const statusTone = isPublished ? 'var(--success)' : 'var(--warning)'
  const statusLabel = site.status ? site.status.charAt(0).toUpperCase() + site.status.slice(1) : 'Draft'

  return (
    <div className="site-card">
      <div className="site-thumb" style={{ background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})` }}>
        {logoUrl && (
          <img src={logoUrl} alt={site.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', padding: 16 }}
            onError={e => { e.currentTarget.style.display = 'none' }} />
        )}
        <div className="site-thumb-overlay">
          <span className="site-status-pill">
            <span className="badge-dot" style={{ color: statusTone }} /> {statusLabel}
          </span>
        </div>
      </div>
      <div className="site-info">
        <div className="site-name-row">
          <span className="site-name" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{site.name}</span>
          <span className="badge" style={{ fontSize: 10, flexShrink: 0 }}>{site.template || site.slug || 'site'}</span>
        </div>
        <div className="site-client">
          <Link2 style={{ width: 12, height: 12 }} /> {domain}
        </div>
        <div className="site-meta-row">
          <Edit style={{ width: 12, height: 12 }} /> Edited {getRelativeTime(site.updated_at)}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          {canEditText ? (
            <Link to={`/client/editor?site=${site.id}`} className="btn btn-sm" style={{ flex: 1, justifyContent: 'center' }}>
              <Edit style={{ width: 12, height: 12 }} /> Edit content
            </Link>
          ) : (
            <Link to={`/client/preview/${site.id}`} className="btn btn-sm" style={{ flex: 1, justifyContent: 'center' }}>
              <Eye style={{ width: 12, height: 12 }} /> Preview
            </Link>
          )}
          {canEditText && (
            <Link to={`/client/preview/${site.id}`} className="site-action" title="Preview">
              <Eye style={{ width: 13, height: 13 }} />
            </Link>
          )}
          {canDeleteSites && (
            <button className="site-action" title="Delete site" style={{ color: 'var(--danger)' }} onClick={() => onDelete(site)}>
              <Trash2 style={{ width: 13, height: 13 }} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────
const ClientSites = ({ openNew = false }) => {
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [siteToDelete, setSiteToDelete] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [showNewSite, setShowNewSite] = useState(openNew)
  const canEditText = useHasPermission('can_edit_text')
  const canDeleteSites = useHasPermission('can_delete_sites')
  const queryClient = useQueryClient()

  const { data: sites, isLoading } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { staleTime: 30000 }
  )

  const deleteSiteMutation = useMutation(
    (siteId) => apiService.delete(`/sites/${siteId}`),
    {
      onSuccess: () => {
        toast.success('Site deleted successfully!')
        queryClient.invalidateQueries(['client-sites'])
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete site')
      }
    }
  )

  const handleDeleteSite = (site) => {
    setSiteToDelete(site)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = () => {
    if (siteToDelete) {
      deleteSiteMutation.mutate(siteToDelete.id)
      setShowDeleteConfirm(false)
      setSiteToDelete(null)
    }
  }

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteConfirm && !event.target.closest('.modal-content')) {
        setShowDeleteConfirm(false)
        setSiteToDelete(null)
      }
    }
    if (showDeleteConfirm) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDeleteConfirm])

  const displaySites = Array.isArray(sites) ? sites : []
  const company = displaySites[0]?.company_name
  const closeNewSite = () => { setShowNewSite(false); if (openNew) navigate('/client/sites') }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '16rem' }}>
        <LoadingSpinner size="lg" text="Loading sites..." />
      </div>
    )
  }

  const statusCounts = {
    all: displaySites.length,
    published: displaySites.filter(s => s.status === 'published').length,
    draft: displaySites.filter(s => s.status === 'draft').length,
    archived: displaySites.filter(s => s.status === 'archived').length,
  }
  const filteredSites = displaySites.filter(s => statusFilter === 'all' ? true : s.status === statusFilter)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>My sites</h1>
          <div className="subtitle">Everything we manage for {company || 'you'}</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewSite(true)}>
          <Plus style={{ width: 14, height: 14 }} /> Request new site
        </button>
      </div>

      {/* Filter bar */}
      <div className="filter-bar" style={{ paddingTop: 8, paddingBottom: 8 }}>
        <div className="segmented">
          {[['all', 'All'], ['published', 'Live'], ['draft', 'Draft'], ['archived', 'Archived']].map(([k, l]) => (
            <button key={k} className={statusFilter === k ? 'on' : ''} onClick={() => setStatusFilter(k)}>
              {l} <span style={{ color: 'var(--text-faint)', marginLeft: 4, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{statusCounts[k] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Sites grid */}
      {filteredSites.length > 0 ? (
        <div className="sites-grid">
          {filteredSites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              canEditText={canEditText}
              canDeleteSites={canDeleteSites}
              onDelete={handleDeleteSite}
            />
          ))}
        </div>
      ) : (
        <div className="card card-pad" style={{ textAlign: 'center' }}>
          <Globe style={{ width: '4rem', height: '4rem', color: 'var(--text-faint)', margin: '0 auto 1rem' }} />
          <h3 className="font-medium" style={{ fontSize: '1.125rem', color: 'var(--text)', marginBottom: '0.5rem' }}>No sites yet</h3>
          <p className="text-muted" style={{ marginBottom: '1.5rem' }}>Get started by requesting your first website</p>
          <button className="btn btn-primary" style={{ display: 'inline-flex' }} onClick={() => setShowNewSite(true)}>
            <Plus style={{ width: '1rem', height: '1rem' }} />
            Request new site
          </button>
        </div>
      )}

      {showNewSite && <NewSiteModal onClose={closeNewSite} />}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && siteToDelete && (
        <div className="modal-backdrop">
          <div className="modal modal-content" style={{ maxWidth: '28rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '3rem', height: '3rem', margin: '0 auto 1rem', background: 'var(--danger-soft)', borderRadius: '50%' }}>
              <Trash2 style={{ width: '1.5rem', height: '1.5rem', color: 'var(--danger)' }} />
            </div>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h3 className="font-semibold" style={{ fontSize: '1.125rem', color: 'var(--text)', marginBottom: '0.5rem' }}>Delete Site</h3>
              <p className="text-soft">
                Are you sure you want to delete <span className="font-medium" style={{ color: 'var(--text)' }}>"{siteToDelete.name}"</span>? This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                className="btn btn-ghost"
                onClick={() => { setShowDeleteConfirm(false); setSiteToDelete(null) }}
                disabled={deleteSiteMutation.isLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteSiteMutation.isLoading}
              >
                {deleteSiteMutation.isLoading ? (
                  <><LoadingSpinner size="sm" />Deleting...</>
                ) : (
                  <><Trash2 style={{ width: '1rem', height: '1rem' }} />Delete Site</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientSites
